// Safe return target Story 1.6 (UX-DR38 / ui-contract.md §5).
//
// Единственный владелец `sessionStorage["freehub.returnTo.v1"]`: четыре реальных
// потребителя (save/read/consume/clear) и точный валидатор. Валидным считается
// только относительный same-origin путь; любое отклонение **удаляет** значение и
// возвращает default `/`, потому что санитизация с сохранением опасного остатка
// запрещена (traversal/encoded-трюки не должны переживать сброс).
//
// Модуль не создаёт второй state-owner и не пишет ничего, кроме этого ключа:
// raw secrets в storage не попадают.
//
// R2: недоступный storage — это отказ, а не успех. Он может проявиться и на
// самом объекте (запрещённый доступ), и на отдельном методе (`getItem`/
// `setItem`/`removeItem`). Любой такой отказ безопасен для вызывающего: очистка
// in-memory состояния и переход логина продолжаются, непроверенный target не
// возвращается, а физическое удаление недоступного storage не выдаётся за
// выполненное.

export const RETURN_TARGET_STORAGE_KEY = "freehub.returnTo.v1";
export const RETURN_TARGET_DEFAULT = "/";
const RETURN_TARGET_MAX_BYTES = 512;

// C2: инвалидация прежнего (до-logout) target. Если безусловная очистка не
// смогла физически удалить значение (недоступный `sessionStorage` или бросающий
// метод), это значение нельзя считать достоверным: оно не возвращается и после
// восстановления доступа В ТОМ ЖЕ runtime. Флаг живёт в памяти единственного
// owner-а модуля и снимается ТОЛЬКО подтверждённой записью нового target —
// отвергнутая запись/неудавшаяся запись его не снимает. Второй store не
// создаётся, в storage по-прежнему пишется ровно один ключ.
let returnTargetInvalidated = false;

function invalidateReturnTarget() {
  returnTargetInvalidated = true;
}

function sessionStore() {
  try {
    return globalThis.sessionStorage ?? null;
  } catch (_error) {
    // Доступ к storage может быть запрещён (privacy mode) — это не успех.
    return null;
  }
}

// R2: отказ может прийти не только на самом объекте (`sessionStorage`
// недоступен), но и на каждом методе уже полученного объекта (privacy mode,
// квота, политика хранения). Такой отказ не прерывает вызывающую очистку и
// никогда не выдаётся за выполненную запись/чтение: безопасная обёртка
// сообщает только «получилось», а не подменяет значение.
function removeValue(store, key) {
  try {
    store.removeItem(key);
    return true;
  } catch (_error) {
    return false;
  }
}

function readValue(store, key) {
  try {
    return store.getItem(key);
  } catch (_error) {
    return null;
  }
}

function writeValue(store, key, value) {
  try {
    store.setItem(key, value);
    return true;
  } catch (_error) {
    return false;
  }
}

function utf8Bytes(value) {
  if (typeof TextEncoder === "function") return new TextEncoder().encode(value).length;
  // Fallback без внешних зависимостей: считаем байты UTF-8 вручную.
  let bytes = 0;
  for (const character of value) {
    const code = character.codePointAt(0);
    if (code <= 0x7f) bytes += 1;
    else if (code <= 0x7ff) bytes += 2;
    else if (code <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

function percentEncodedOctetsAreSafe(value) {
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] !== "%") continue;
    const hex = value.slice(index + 1, index + 3);
    if (!/^[0-9a-fA-F]{2}$/.test(hex)) return false;
    const octet = Number.parseInt(hex, 16);
    // Декодированный октет не может быть `.`, `/`, `\` или control-байтом:
    // иначе `%2e%2e`, `%2f`, `%5c`, `%00` обходили бы проверку сегментов.
    if (octet === 0x2e || octet === 0x2f || octet === 0x5c) return false;
    if (octet <= 0x1f || octet === 0x7f) return false;
    index += 2;
  }
  return true;
}

function hasControlOrBackslash(value) {
  for (const character of value) {
    const code = character.codePointAt(0);
    if (code <= 0x1f || code === 0x7f) return true;
    if (character === "\\") return true;
  }
  return false;
}

/**
 * Декодирует percent-escapes сегмента для semantic-сравнения.
 *
 * Все `%` уже проверены [`percentEncodedOctetsAreSafe`], поэтому здесь есть ровно
 * пары hex-разрядов и октеты `.`/`/`/`\`/control исключены. Отдельный код нужен,
 * потому что запрет `/api` и `/login` относится к **семантике** первого сегмента,
 * а не к его spelling: `/%61pi/auth` обязан отклоняться так же, как `/api/auth`.
 */
function decodePercentEscapes(value) {
  let decoded = "";
  for (let index = 0; index < value.length; index += 1) {
    if (value[index] === "%") {
      decoded += String.fromCharCode(Number.parseInt(value.slice(index + 1, index + 3), 16));
      index += 2;
      continue;
    }
    decoded += value[index];
  }
  return decoded;
}

/**
 * Валидирует safe return target. Возвращает true только для canonical
 * относительного same-origin пути.
 */
export function isValidReturnTarget(value) {
  if (typeof value !== "string" || value.length === 0) return false;
  if (utf8Bytes(value) > RETURN_TARGET_MAX_BYTES) return false;
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.includes("://")) return false;
  if (hasControlOrBackslash(value)) return false;
  if (!percentEncodedOctetsAreSafe(value)) return false;

  const path = value.split("?", 1)[0].split("#", 1)[0];
  const segments = path.split("/");
  if (segments.some((segment) => segment === "." || segment === "..")) return false;
  const first = segments[1] ?? "";
  // `/api` (включая точное `/api`, `/api?x=1`, `/api/`) и `/login` не являются
  // целями возврата — в том числе в percent-encoded написании (`/%61pi`,
  // `/%6cogin/x`): сравнение идёт по декодированному первому сегменту, потому
  // что сервер декодирует путь перед маршрутизацией. Санитизация, сохраняющая
  // опасный остаток, запрещена: значение именно отклоняется.
  const firstDecoded = decodePercentEscapes(first).toLowerCase();
  if (firstDecoded === "api" || firstDecoded === "login") return false;
  return true;
}

/** Сохраняет валидный target; невалидное значение удаляет, а не «чистит». */
export function saveReturnTarget(path) {
  const store = sessionStore();
  if (store === null) return RETURN_TARGET_DEFAULT;
  if (!isValidReturnTarget(path)) {
    removeValue(store, RETURN_TARGET_STORAGE_KEY);
    return RETURN_TARGET_DEFAULT;
  }
  // Отказ записи не выдаётся за сохранение: без подтверждённой записи целевой
  // путь не гарантирован, поэтому возвращается безопасный default. Такой отказ
  // НЕ снимает инвалидацию (C2): доступным становится только подтверждённо
  // записанный новый target.
  if (!writeValue(store, RETURN_TARGET_STORAGE_KEY, path)) {
    return RETURN_TARGET_DEFAULT;
  }
  returnTargetInvalidated = false;
  return path;
}

/** Читает target; невалидное значение удаляется, возвращается default. */
export function readReturnTarget() {
  const store = sessionStore();
  if (store !== null && returnTargetInvalidated) {
    // C2: прежнее (до-logout) значение недостоверно — даже когда доступ к
    // storage вернулся. Оно не возвращается; удаление остаётся best-effort.
    removeValue(store, RETURN_TARGET_STORAGE_KEY);
    return RETURN_TARGET_DEFAULT;
  }
  if (store === null) return RETURN_TARGET_DEFAULT;
  const stored = readValue(store, RETURN_TARGET_STORAGE_KEY);
  if (stored === null) return RETURN_TARGET_DEFAULT;
  if (!isValidReturnTarget(stored)) {
    removeValue(store, RETURN_TARGET_STORAGE_KEY);
    return RETURN_TARGET_DEFAULT;
  }
  return stored;
}

/** Читает + валидирует + удаляет: ровно одно потребление на вход. */
export function consumeReturnTarget() {
  const store = sessionStore();
  if (store === null) return RETURN_TARGET_DEFAULT;
  const target = readReturnTarget();
  // Недоступное удаление не отменяет уже проверенный target (валидатор его
  // подтвердил) и не превращает его в непроверенный.
  removeValue(store, RETURN_TARGET_STORAGE_KEY);
  return target;
}

/**
 * Безусловное удаление: подтверждённый выход, end-all, password change, expiry.
 *
 * Возвращает `true` только если удаление действительно выполнено. `false`
 * означает, что storage недоступен: очистка in-memory состояния продолжается,
 * но физическое удаление не выдаётся за выполненное (R2), а прежний target
 * помечается инвалидированным (C2) — иначе восстановление доступа воскресило бы
 * уже сброшенный адрес возврата.
 */
export function clearReturnTarget() {
  const store = sessionStore();
  if (store === null) {
    invalidateReturnTarget();
    return false;
  }
  if (removeValue(store, RETURN_TARGET_STORAGE_KEY)) return true;
  invalidateReturnTarget();
  return false;
}
